import { Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '@/shared/stores/authStore';
import { ROUTES } from '@/shared/config/constants';
import { ParticleSphere, Skeleton } from '@/shared/ui';

// Splash skeleton durante a hidratacao da sessao -- replica o layout real
// da LoginPage (grid 2 colunas no desktop, single col no mobile, com
// aside escuro + sphere a esquerda e form a direita). Evita o "card no
// meio do nada" que dava sensacao de loading generico.
const AuthSplashSkeleton = () => (
  <main
    className="flex min-h-dvh w-full flex-col bg-white lg:grid lg:grid-cols-2"
    aria-busy="true"
    aria-label="Verificando sessão"
  >
    <header className="flex items-center justify-center px-6 pt-[calc(env(safe-area-inset-top)+24px)] pb-2 lg:hidden">
      <Skeleton className="h-16 w-44 rounded-md" />
    </header>

    <aside className="relative hidden min-h-dvh flex-col overflow-hidden bg-black text-white lg:flex">
      <div className="relative z-10 flex items-center gap-3 p-10">
        <Skeleton className="h-9 w-40 rounded-md bg-white/10" />
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <ParticleSphere className="aspect-square h-full max-h-[120%] w-full max-w-[120%]" />
      </div>
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black via-black/70 to-transparent"
        aria-hidden="true"
      />
      <div className="relative z-10 mt-auto flex flex-col gap-3 p-10">
        <Skeleton className="h-9 w-72 bg-white/10" />
        <Skeleton className="h-4 w-80 bg-white/10" />
        <Skeleton className="h-4 w-60 bg-white/10" />
      </div>
    </aside>

    <section className="flex flex-1 items-center justify-center px-6 py-6 sm:px-10 lg:min-h-dvh lg:py-16">
      <div className="w-full max-w-md">
        <header className="flex flex-col gap-2">
          <Skeleton className="h-9 w-72 md:h-10" />
          <Skeleton className="h-4 w-80" />
        </header>

        <div className="mt-10 flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-11 w-full rounded-lg" />
          </div>
          <div className="flex flex-col gap-1.5">
            <div className="flex w-full items-center justify-between gap-3">
              <Skeleton className="h-3 w-14" />
              <Skeleton className="h-3 w-32" />
            </div>
            <Skeleton className="h-11 w-full rounded-lg" />
          </div>
          <Skeleton className="h-11 w-full rounded-lg bg-gray-900/80" />
        </div>

        <div className="mt-8 flex items-center justify-center gap-2">
          <Skeleton className="h-3 w-28" />
          <Skeleton className="h-3 w-20" />
        </div>
      </div>
    </section>
  </main>
);

export const PublicRoute = () => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isHydrating = useAuthStore((s) => s.isHydrating);
  const user = useAuthStore((s) => s.user);

  if (isHydrating) return <AuthSplashSkeleton />;

  if (isAuthenticated && user) {
    return (
      <Navigate to={user.onboardingCompleto ? ROUTES.APP.MATCHES : ROUTES.ONBOARDING} replace />
    );
  }

  return <Outlet />;
};
